import { useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Input, message, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { DeleteOutlined, DownloadOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import { api } from "../services/apiClient";

const { Title, Text, Paragraph } = Typography;
const CAPABILITIES = ["METADATA", "DOWNLOAD", "RENDER_FFMPEG", "UPLOAD_YOUTUBE", "UPLOAD_FACEBOOK", "UPLOAD_TIKTOK"];
const WORKER_PLATFORM_OPTIONS = [
  { value: "windows", label: "Windows" },
  { value: "linux", label: "Ubuntu / Linux (x64)" },
  { value: "macos-arm64", label: "macOS (Apple Silicon)" },
  { value: "macos-x64", label: "macOS (Intel)" },
  { value: "all", label: "Full all OS" },
];
const STATUS_OPTIONS = ["PENDING", "ACTIVE", "REVOKED"].map((value) => ({ value, label: value }));

function formatDateTime(value) {
  if (!value) return "-";
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "-";
}

function userLabel(item) {
  if (!item) return "-";
  const name = item.username || item.email || item.userId;
  return item.email && item.username ? `${item.username} (${item.email})` : name;
}

function machineInfo(item) {
  return (
    <div className="worker-machine-info">
      <Text>{item.osName || "-"}</Text>
      {item.hostname ? (
        <Text type="secondary" className="worker-machine-line">
          {`Tên máy: ${item.hostname}`}
        </Text>
      ) : null}
    </div>
  );
}

function saveBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function AutoWorkerAgents() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [downloading, setDownloading] = useState(null);
  const [downloadTarget, setDownloadTarget] = useState(null);
  const [downloadPlatform, setDownloadPlatform] = useState("windows");
  const [editingAgent, setEditingAgent] = useState(null);
  const [savingAgent, setSavingAgent] = useState(false);
  const [open, setOpen] = useState(false);
  const [tokenResult, setTokenResult] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [agents, userOptions] = await Promise.all([
        api.get("/api/worker-agents"),
        api.get("/api/users/options"),
      ]);
      setItems(agents || []);
      setUsers(userOptions || []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    try {
      const values = await form.validateFields();
      const result = await api.post("/api/worker-agents", {
        ...values,
        serviceCode: "AUTO_UPLOAD",
        capabilities: CAPABILITIES,
      });
      setTokenResult(result);
      setOpen(false);
      form.resetFields();
      load();
    } catch (error) {
      if (error.message) message.error(error.message);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/api/worker-agents/${id}`);
      message.success("Đã thu hồi worker");
      load();
    } catch (error) {
      message.error(error.message);
    }
  };

  const downloadPackage = async (item, type = "package") => {
    try {
      setDownloading({ id: item.id, type });
      const path = type === "engine"
        ? `/api/worker-agents/${item.id}/engine`
        : `/api/worker-agents/${item.id}/package?platform=${encodeURIComponent(downloadPlatform)}`;
      const result = await api.download(path);
      saveBlob(result.blob, result.fileName);
      setDownloadTarget(null);
      message.success(type === "engine" ? "Đã tải engine worker" : "Đã tải gói worker");
    } catch (error) {
      message.error(error.message);
    } finally {
      setDownloading(null);
    }
  };

  const setupText = useMemo(() => {
    if (!tokenResult?.activationToken) return "";
    return [
      "app.worker.activation-token=" + tokenResult.activationToken,
      "app.worker.service-code=AUTO_UPLOAD",
      "app.worker.worker-name=" + (tokenResult.workerName || "Auto Upload Worker"),
    ].join("\n");
  }, [tokenResult]);

  const userOptions = users.map((user) => ({
    value: user.id,
    label: user.email && user.username ? `${user.username} (${user.email})` : user.email || user.username || user.id,
  }));

  const filteredItems = useMemo(() => {
    const keyword = userSearch.trim().toLowerCase();
    if (!keyword) return items;
    return items.filter((item) =>
      [item.username, item.email, item.userId]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword))
    );
  }, [items, userSearch]);

  const openEditAgent = (item) => {
    setEditingAgent(item);
    editForm.setFieldsValue({
      workerName: item.workerName,
      status: item.status || "PENDING",
    });
  };

  const saveAgent = async () => {
    try {
      const values = await editForm.validateFields();
      setSavingAgent(true);
      await api.put(`/api/worker-agents/${editingAgent.id}`, values);
      message.success("?? c?p nh?t worker");
      setEditingAgent(null);
      load();
    } catch (error) {
      if (error.message) message.error(error.message);
    } finally {
      setSavingAgent(false);
    }
  };

  const columns = [
    {
      title: "USER",
      width: 180,
      render: (_, item) => (
        <div>
          <Text strong>{item.username || item.email || "-"}</Text>
          {item.email && item.username ? <><br /><Text type="secondary">{item.email}</Text></> : null}
        </div>
      ),
    },
    {
      title: "WORKER",
      dataIndex: "workerName",
      width: 170,
      render: (value, item) => (
        <div>
          <Text strong>{value}</Text>
          <br />
          <Text type="secondary">{item.status === "ACTIVE" ? "Đã kích hoạt" : "Chưa kích hoạt"}</Text>
        </div>
      ),
    },
    { title: "DỊCH VỤ", width: 120, dataIndex: "serviceCode", render: (value) => <Tag color="blue">{value}</Tag> },
    { title: "TRẠNG THÁI", width: 110, dataIndex: "status", render: (value) => <Tag color={value === "ACTIVE" ? "green" : value === "PENDING" ? "gold" : "default"}>{value}</Tag> },
    { title: "MÁY", width: 260, render: (_, item) => machineInfo(item) },
    { title: "KÍCH HOẠT", width: 150, dataIndex: "activatedAt", render: formatDateTime },
    { title: "ONLINE GẦN NHẤT", width: 160, dataIndex: "lastHeartbeatAt", render: formatDateTime },
    {
      title: "HÀNH ĐỘNG",
      width: 170,
      render: (_, item) => (
        <Space className="provider-actions" size={4}>
          <Tooltip title="Sửa worker">
            <Button
              aria-label="Sửa worker"
              className="provider-action-button"
              icon={<EditOutlined />}
              onClick={() => openEditAgent(item)}
              type="text"
            />
          </Tooltip>
          <Popconfirm title="Thu hồi worker này?" okText="Thu hồi" cancelText="Đóng" onConfirm={() => revoke(item.id)}>
            <Tooltip title="Thu hồi worker">
              <Button aria-label="Thu hồi worker" className="provider-action-button" danger icon={<DeleteOutlined />} type="text" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-heading">
        <div>
          <Title level={3}>Worker Agents</Title>
          <Text type="secondary">Tạo gói worker để người dùng tải về và chạy trên máy riêng.</Text>
        </div>
      </div>
      <Card className="criclebox">
        <div className="auto-toolbar">
          <Input
            allowClear
            className="auto-toolbar-search"
            onChange={(event) => setUserSearch(event.target.value)}
            placeholder="Tìm theo user"
            prefix={<SearchOutlined />}
            value={userSearch}
          />
          <Button icon={<ReloadOutlined />} onClick={load}>Làm mới</Button>
          <Button className="auto-toolbar-primary" type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>Tạo agent</Button>
        </div>
        <Table rowKey="id" loading={loading} columns={columns} dataSource={filteredItems} tableLayout="fixed" />
      </Card>

      <Modal title="Tạo worker agent" visible={open} onOk={create} onCancel={() => setOpen(false)} okText="Tạo agent" cancelText="Đóng">
        <Form form={form} layout="vertical" initialValues={{ workerName: "Auto Upload Worker" }}>
          <Form.Item name="userId" label="User" rules={[{ required: true, message: "Chọn user chạy worker" }]}>
            <Select
              showSearch
              placeholder="Chọn user"
              optionFilterProp="label"
              options={userOptions}
            />
          </Form.Item>
          <Form.Item name="workerName" label="Tên worker" rules={[{ required: true, message: "Nhập tên worker" }]}>
            <Input placeholder="VD: Máy upload tại nhà" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Worker agent đã tạo"
        visible={Boolean(tokenResult)}
        onCancel={() => setTokenResult(null)}
        footer={(
          <Space>
            <Button onClick={() => setTokenResult(null)}>Đóng</Button>
            <Button type="primary" icon={<DownloadOutlined />} loading={downloading?.id === tokenResult?.id} onClick={() => setDownloadTarget(tokenResult || {})}>
              Tải gói worker
            </Button>
          </Space>
        )}
        width={760}
      >
        <Text type="secondary">Gói tải về đã gồm file worker, run.bat, run.sh và cấu hình token. Người dùng chỉ cần giải nén rồi chạy run.bat.</Text>
        <Paragraph copyable style={{ marginTop: 12, whiteSpace: "pre-wrap" }}>{setupText}</Paragraph>
        <Text type="secondary">User: {userLabel(tokenResult)}</Text>
      </Modal>

      <Modal
        title="Sửa worker agent"
        visible={Boolean(editingAgent)}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={savingAgent}
        onCancel={() => setEditingAgent(null)}
        onOk={saveAgent}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="workerName" label="Tên worker" rules={[{ required: true, message: "Nhập tên worker" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="status" label="Trạng thái" rules={[{ required: true, message: "Chọn trạng thái" }]}>
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Download Worker Agent"
        visible={Boolean(downloadTarget)}
        onCancel={() => {
          if (!downloading) setDownloadTarget(null);
        }}
        closable={!downloading}
        maskClosable={!downloading}
        footer={(
          <Space>
            <Button disabled={Boolean(downloading)} onClick={() => setDownloadTarget(null)}>Đóng</Button>
            <Button disabled={Boolean(downloading)} loading={downloading?.id === downloadTarget?.id && downloading?.type === "engine"} onClick={() => downloadPackage(downloadTarget || {}, "engine")}>Download engine</Button>
            <Button disabled={Boolean(downloading)} type="primary" loading={downloading?.id === downloadTarget?.id && downloading?.type === "package"} onClick={() => downloadPackage(downloadTarget || {}, "package")}>Download full</Button>
          </Space>
        )}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Select
            disabled={Boolean(downloading)}
            onChange={setDownloadPlatform}
            options={WORKER_PLATFORM_OPTIONS}
            style={{ width: "100%" }}
            value={downloadPlatform}
          />
          <Text>Download full để cài mới đầy đủ. Download engine chỉ tải file <b>application.jar</b>, dùng để ghi đè vào thư mục worker hiện tại.</Text>
        </Space>
      </Modal>
    </>
  );
}
